import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

interface Props {
  errorMessage?: string
  disabled: boolean
  signInWithGoogle: () => void
  signInWithEmail: (email: string, password: string) => void
  createAccount: (email: string, password: string) => void
  mode: 'login' | 'signup'
}

export const LoginForm = ({
  errorMessage,
  disabled,
  signInWithGoogle,
  signInWithEmail,
  createAccount,
  mode,
}: Props) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (mode === 'login') {
      signInWithEmail(email, password)
    } else {
      createAccount(email, password)
    }
  }

  const handleToggleMode = () => {
    const from = searchParams.get('from') || ''
    const newPath = mode === 'login' ? '/sign-up' : '/login'
    navigate(`${newPath}${from ? `?from=${from}` : ''}`)
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 w-full">
      <div className="flex flex-col items-center justify-center space-y-6 shadow-lg p-8 bg-white rounded-xl">
        <h2 className="text-2xl font-bold text-gray-800">
          {mode === 'login'
            ? 'Welcome back, sign in to continue'
            : 'Create your account'}
        </h2>

        <form onSubmit={handleSubmit} className="w-full space-y-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={disabled}
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={disabled}
          />
          <button
            type="submit"
            className={`w-full px-6 py-3 text-lg font-medium text-white bg-blue-600 rounded-lg transition-all duration-200
            ${
              disabled
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-blue-700 hover:shadow-md'
            }`}
            disabled={disabled}
          >
            {mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <button
          onClick={handleToggleMode}
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          {mode === 'login'
            ? "Don't have an account? Create one"
            : 'Already have an account? Sign in'}
        </button>

        <div className="flex items-center w-full">
          <div className="flex-1 border-t border-gray-300"></div>
          <span className="px-4 text-gray-500">or</span>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>

        <button
          className={`flex items-center justify-center w-full gap-3 px-6 py-3 text-lg font-medium text-gray-700 bg-white border border-gray-300 rounded-lg transition-all duration-200
          ${
            disabled
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-gray-50 hover:shadow-md'
          }`}
          disabled={disabled}
          onClick={signInWithGoogle}
        >
          {mode === 'login' ? 'Sign In with Google' : 'Sign Up with Google'}
        </button>

        {errorMessage && (
          <p className="px-4 py-2 text-sm text-red-600 bg-red-50 rounded-lg">
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  )
}
